import { isAxiosError } from 'axios';
import { OutboundCarrierGateError } from './fulfillmentOutboundGate';
import type {
  AdjustStockRefusal,
  BelowReservedRefusal,
  ClosedStockTakeRefusal,
  StaleStockLine,
  StaleStockRefusal,
} from '@/infrastructure/FulfillmentAdminClient';

interface AdminActionErrorBody {
  message?: string | string[];
  /** The outbound gate's itemised reasons, one English sentence per failed check. */
  violations?: unknown;
}

/** ValidationPipe answers with a message array, so both shapes have to read as one line. */
const asOneLine = (message?: string | string[]): string | undefined => {
  const text = Array.isArray(message) ? message.join(' · ') : message;
  return text || undefined;
};

/**
 * What the server (or Admin outbound preflight) said — an operator cannot tell a wrong line number
 * from a line that was never shipped unless the refusal reaches the screen.
 */
export const serverRefusal = (error: unknown): string | undefined =>
  error instanceof OutboundCarrierGateError
    ? error.message
    : isAxiosError<AdminActionErrorBody>(error)
      ? asOneLine(error.response?.data?.message)
      : undefined;

/** The outbound gate's violations a refusal carried, or nothing when it carried none. */
export const serverViolations = (error: unknown): string[] | undefined => {
  const violations = isAxiosError<AdminActionErrorBody>(error) ? error.response?.data?.violations : undefined;
  const lines = Array.isArray(violations)
    ? violations.filter((line): line is string => typeof line === 'string' && line !== '')
    : [];
  return lines.length > 0 ? lines : undefined;
};

/** A hold placed without a reason still holds; the server then writes the reason as the literal "null". */
const describeHoldReason = (reason: string | null | undefined): string => {
  const text = reason?.trim();
  return text && text !== 'null' && text !== 'undefined' ? text : 'no reason recorded';
};

interface ViolationReading {
  pattern: RegExp;
  /** Which gate failed, short enough for a badge. */
  badge: string;
  /** The same violation as a desk reads it; absent means the server's sentence already reads plainly. */
  plain?: (match: RegExpExecArray) => string;
}

/** Server violations are per-line and English; the badge says which gate failed, not how many times. */
const VIOLATION_READINGS: ViolationReading[] = [
  { pattern: /^wc status/i, badge: 'WC pending' },
  {
    pattern: /hold/i,
    badge: 'Hold',
    plain: (match) => {
      const held = /^order is on hold:?\s*(.*)$/i.exec(match.input);
      return held ? `on hold: ${describeHoldReason(held[1])}` : match.input;
    },
  },
  // A shipped line is not an inbound problem: naming it "incomplete" sent the picker looking for goods that already left.
  {
    pattern: /^line (\d+) is OUTBOUNDED/i,
    badge: 'Already outbounded',
    plain: (match) => `line ${match[1]} is already outbounded`,
  },
  { pattern: /^line (\d+) is/i, badge: 'Awaiting inbound', plain: (match) => `line ${match[1]} is awaiting inbound` },
];

const readingOf = (violation: string): { reading: ViolationReading; match: RegExpExecArray } | undefined => {
  for (const reading of VIOLATION_READINGS) {
    const match = reading.pattern.exec(violation);
    if (match) {
      return { reading, match };
    }
  }
  return undefined;
};

/** The badge for one violation, or the violation itself when no gate claims it. */
const describeViolation = (violation: string): string => readingOf(violation)?.reading.badge ?? violation;

export const violationBadges = (violations: string[]): string[] => [...new Set(violations.map(describeViolation))];

/** Every violation in plain words — the hold names its reason, a line keeps its number — as one line. */
export const describeViolations = (violations: string[]): string => {
  const text = [
    ...new Set(
      violations.map((violation) => {
        const found = readingOf(violation);
        return found?.reading.plain ? found.reading.plain(found.match) : violation;
      }),
    ),
  ].join(' · ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/** The hold an order detail carries, in the words the outbound gate would refuse it with; null when it is not held. */
export const describeHold = (order: { heldAt?: string | null; holdReason?: string | null }): string | null =>
  // The stamp decides, as on the server: a hold with an empty reason still leaves the order held.
  order.heldAt != null || order.holdReason != null ? `On hold — ${describeHoldReason(order.holdReason)}` : null;

/** The server's own words when it gave any, and the caller's sentence when the wire carried none. */
export const describeFailure = (error: unknown, fallback: string): string => serverRefusal(error) ?? fallback;

/** The stale-stock refusal a count or confirm answered with, or nothing when the failure was anything else. */
export const staleStockRefusalOf = (error: unknown): StaleStockRefusal | null => {
  if (!isAxiosError<Partial<StaleStockRefusal>>(error) || error.response?.status !== 409) {
    return null;
  }
  const body = error.response.data;
  return body?.code === 'STALE_STOCK' && Array.isArray(body.staleLines) && body.staleLines.length > 0
    ? { code: 'STALE_STOCK', docId: body.docId ?? '', staleLines: body.staleLines }
    : null;
};

/**
 * The 409 a live audit count answers when it would cut below the shelf's reservations and nobody acknowledged it
 * [SSD-7447, SSD-7437 3·4]; nothing was written. Anything else is null.
 */
export const belowReservedRefusalOf = (error: unknown): BelowReservedRefusal | null => {
  if (!isAxiosError<Partial<BelowReservedRefusal>>(error) || error.response?.status !== 409) {
    return null;
  }
  const body = error.response.data;
  return body?.code === 'BELOW_RESERVED' && typeof body.reserved === 'number'
    ? {
        code: 'BELOW_RESERVED',
        sku: body.sku ?? '',
        locationCode: body.locationCode ?? '',
        countedQty: Number(body.countedQty ?? 0),
        reserved: body.reserved,
        holders: Array.isArray(body.holders) ? body.holders : [],
      }
    : null;
};

/** Names every SKU whose shelf moved, with the balance it was counted against, the one it holds now, and who moved it. */
export const describeStaleStock = (lines: StaleStockLine[]): string =>
  `Stock changed for ${lines
    .map((line) => {
      const by = line.changedBy ? `, changed by ${line.changedBy}` : '';
      // Only the balance version moved: the shelf was changed and came back to the same number [SSD-7447].
      return line.countedAgainst === line.current
        ? `SKU ${line.sku} (moved and came back to ${line.current}${by})`
        : `SKU ${line.sku} (counted against ${line.countedAgainst}, now ${line.current}${by})`;
    })
    .join(', ')}.`;

/** The closed-round refusal a count or confirm answered with, or nothing when the failure was anything else. */
export const closedStockTakeRefusalOf = (error: unknown): ClosedStockTakeRefusal | null => {
  if (!isAxiosError<Partial<ClosedStockTakeRefusal>>(error) || error.response?.status !== 409) {
    return null;
  }
  const body = error.response.data;
  return body?.code === 'STOCK_TAKE_CLOSED'
    ? {
        code: 'STOCK_TAKE_CLOSED',
        message: body.message || "This shelf's count was already closed by someone else.",
        docId: body.docId ?? '',
        status: body.status ?? '',
        ...(body.closedBy ? { closedBy: body.closedBy } : {}),
      }
    : null;
};

/** What happened to the round, in the server's own plain words: it was written for the desk, so it is shown as is. */
export const describeClosedStockTake = (refusal: ClosedStockTakeRefusal): string => refusal.message;

/**
 * Whether an abandon was refused because the round had already closed: a colleague confirmed it, or it was
 * closed some other way. The cancel route answers that with a plain 409 sentence ("… is already CONFIRMED and
 * can no longer be abandoned"); a coded STOCK_TAKE_CLOSED reads the same. Either way nothing is left to abandon.
 */
export const isRoundAlreadyClosed = (error: unknown): boolean => {
  if (closedStockTakeRefusalOf(error)) {
    return true;
  }
  const message = isAxiosError(error) && error.response?.status === 409 ? serverRefusal(error) : undefined;
  return message !== undefined && /\balready\b[\s\S]*\bno longer be abandoned\b/i.test(message);
};

/** The coded 409 the manual stock route refused with, or nothing when the failure was anything else (SSD-7099). */
export const adjustStockRefusalOf = (error: unknown): AdjustStockRefusal | null => {
  if (!isAxiosError<Partial<AdjustStockRefusal>>(error) || error.response?.status !== 409) {
    return null;
  }
  const body = error.response.data as Partial<AdjustStockRefusal> | undefined;
  switch (body?.code) {
    case 'MANUAL_STOCK_BEFORE_LAUNCH':
      return body as AdjustStockRefusal;
    // A shortfall without its figures is read as an uncoded refusal, so the server's sentence still reaches the screen.
    case 'INSUFFICIENT_STOCK':
    case 'RESERVED_STOCK':
      return 'onHand' in body && typeof body.onHand === 'number' && typeof body.reserved === 'number'
        ? (body as AdjustStockRefusal)
        : null;
    case 'LOCATION_OCCUPIED':
      return 'occupiedBy' in body && typeof body.occupiedBy === 'string' ? (body as AdjustStockRefusal) : null;
    default:
      return null;
  }
};
